/**
 * The 6-step calculator wizard + results wiring. Revised layout (per
 * tcc_ui_revised.html): conversational titles, plain-language option
 * descriptions, a "Step N of 7 · <label>" header (no org watermark / number
 * squares) and Back/Next navigation at the BOTTOM of the card.
 *
 * Modes (from the top-right selector):
 *   easy   — simplified; advanced controls hidden and defaulted.
 *   expert — exposes bulk size, model, pricing tier, caching, thinking tokens.
 *   test   — expert visibility + a test-case dropdown that pre-fills the
 *            wizard from AI_Calculator_Test_Cases.docx for QA vs. the Excel tab.
 */

import type { Currency, EstimateOverrides, EstimateResult, EstimationParams, ModelInfo } from '@tcc/shared';
import { deriveVolumetrics } from '@tcc/shared';
import { useEffect, useRef, useState } from 'react';
import {
	calculateEstimate,
	ModelUnavailableError,
	ServiceUnavailableError,
	ValidationApiError,
} from '../../api/client.js';
import { STEP_DOTS, STEP_TITLE, VOL_MAX } from '../../constants.js';
import { initialWizardState, TOTAL_INPUT_STEPS, toEstimateInput, type WizardState } from '../../state/state.js';
import {
	Step1Content,
	Step2Interaction,
	Step3Inputs,
	Step4Context,
	Step5Decisions,
	Step6Output,
	WizardSidebar,
} from '../Steps/Steps';
import { ResultsScreen } from '../Summary/Summary.js';

export type CalcMode = 'easy' | 'expert' | 'test';

/** Conversational question titles shown above each step. */
const STEP_Q = [
	'What content does the AI work with?',
	'How will your process use the AI?',
	'How many inputs — and how big is each one?',
	'Does each input include extra context?',
	'Does the process make decisions?',
	"How big is the AI's response?",
];

/**
 * QA test cases from AI_Calculator_Test_Cases.docx. Each pre-fills the wizard so
 * the app's estimate can be compared against the Excel Guided tab. The 3-tier
 * Low/Med/High decisions are mapped onto the app's Simple/Complex model
 * (Low → Simple, Med + High → Complex) — an intentional, documented divergence.
 */
const TEST_CASES: { id: string; label: string; patch: Partial<WizardState> }[] = [
	{
		id: 'tc1',
		label: 'TC1 · Baseline text assistant (no decisions)',
		patch: {
			contentTypes: ['text'],
			interactionType: 'assistive',
			volumetrics: deriveVolumetrics('perMonth', 2100),
			dataUnits: { ...initialWizardState.dataUnits, textPages: 5 },
			additionalContext: { enabled: false, unit: 'page', count: 1 },
			decisionMaking: { choice: 'no', decisionsSimple: 0, decisionsComplex: 0 },
			outputSize: 'paragraph',
			bulkSize: 1,
		},
	},
	{
		id: 'tc2',
		label: 'TC2 · Bulk processing (50 / request)',
		patch: {
			contentTypes: ['text'],
			interactionType: 'batch',
			volumetrics: deriveVolumetrics('perMonth', 5000),
			dataUnits: { ...initialWizardState.dataUnits, textPages: 1 },
			additionalContext: { enabled: false, unit: 'page', count: 1 },
			decisionMaking: { choice: 'no', decisionsSimple: 0, decisionsComplex: 0 },
			outputSize: 'paragraph',
			bulkSize: 50,
		},
	},
	{
		id: 'tc3',
		label: 'TC3 · Decision-heavy (Simple 2 / Complex 2)',
		patch: {
			contentTypes: ['text'],
			interactionType: 'assistive',
			volumetrics: deriveVolumetrics('perMonth', 1000),
			dataUnits: { ...initialWizardState.dataUnits, textPages: 2 },
			additionalContext: { enabled: false, unit: 'page', count: 1 },
			// 2 simple + 2 complex decisions per input.
			decisionMaking: { choice: 'yes', decisionsSimple: 2, decisionsComplex: 2 },
			outputSize: 'paragraph',
			bulkSize: 1,
		},
	},
	{
		id: 'tc4',
		label: 'TC4 · Image inputs, bulk (Simple 1 decision)',
		patch: {
			contentTypes: ['images'],
			interactionType: 'batch',
			volumetrics: deriveVolumetrics('perDay', 300),
			dataUnits: { ...initialWizardState.dataUnits, imageCount: 1, imageSize: 'large' },
			additionalContext: { enabled: false, unit: 'page', count: 1 },
			decisionMaking: { choice: 'yes', decisionsSimple: 1, decisionsComplex: 0 },
			outputSize: 'sentence',
			bulkSize: 20,
		},
	},
	{
		id: 'tc5',
		label: 'TC5 · Full complexity (context doc + Complex decision)',
		patch: {
			contentTypes: ['text'],
			interactionType: 'assistive',
			volumetrics: deriveVolumetrics('perDay', 50),
			dataUnits: { ...initialWizardState.dataUnits, textPages: 3 },
			additionalContext: { enabled: true, unit: 'document', count: 1 },
			// 1 complex decision.
			decisionMaking: { choice: 'yes', decisionsSimple: 0, decisionsComplex: 1 },
			outputSize: 'page',
			bulkSize: 1,
		},
	},
];

export interface WizardProps {
	models: ModelInfo[];
	params: EstimationParams;
	/** UI mode: easy (default) hides advanced fields; expert shows them; test adds the test-case dropdown. */
	mode?: CalcMode;
	/** Output display currency (controlled from the top-right selector). */
	currency?: Currency;
	/** Visual layout: 'classic' (top progress bar) or 'clean' (left step sidebar). */
	uiVersion?: 'classic' | 'clean';
	/** Reports a live monthly estimate as the user progresses (drives the always-on top-bar pill). */
	onLiveEstimate?: (v: { monthly: number; currency: Currency } | null) => void;
}

export function Wizard({
	models,
	params,
	mode = 'easy',
	currency = 'GBP',
	uiVersion = 'classic',
	onLiveEstimate,
}: WizardProps) {
	const expert = mode !== 'easy'; // expert + test both show advanced fields
	const testMode = mode === 'test';
	const clean = uiVersion === 'clean';

	const [state, setState] = useState<WizardState>(initialWizardState);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<EstimateResult | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [announcement, setAnnouncement] = useState('');
	const [testCaseId, setTestCaseId] = useState('');
	// Per-interaction input tokens from the latest live estimate (for the bulk hint).
	const [liveInput, setLiveInput] = useState<number | null>(null);
	const stepHeadingRef = useRef<HTMLHeadingElement>(null);

	// Default the model selection once models load (from the Configure tab / API).
	useEffect(() => {
		setState((s) => (s.modelId ? s : { ...s, modelId: models[0]?.modelId }));
	}, [models]);

	// Announce step changes and move focus to step heading for screen readers.
	useEffect(() => {
		if (state.stepIndex < TOTAL_INPUT_STEPS) {
			setAnnouncement(`Step ${state.stepIndex + 1} of ${TOTAL_INPUT_STEPS}: ${STEP_TITLE[state.stepIndex]}`);
			setTimeout(() => stepHeadingRef.current?.focus(), 100);
		}
	}, [state.stepIndex]);

	// Build per-request overrides: edited general params + the selected (edited) model.
	const overrides = (): EstimateOverrides => ({
		params,
		model: models.find((m) => m.modelId === state.modelId),
	});

	// Recompute the live result when the output currency changes (top-right selector).
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only re-runs on currency change.
	useEffect(() => {
		if (!result) return;
		let cancelled = false;
		void (async () => {
			setSubmitting(true);
			try {
				const r = await calculateEstimate({ ...toEstimateInput(state), currency, overrides: overrides() });
				if (!cancelled) {
					setResult(r);
					setError(null);
				}
			} catch {
				if (!cancelled) setError('Could not recompute for that currency.');
			} finally {
				if (!cancelled) setSubmitting(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [currency]);

	// Always-on live estimate for the top-bar pill. Debounced; skipped entirely
	// when no callback is provided (e.g. in unit tests) or before models load.
	// biome-ignore lint/correctness/useExhaustiveDependencies: overrides() is derived from these deps.
	useEffect(() => {
		if (!onLiveEstimate || models.length === 0) return;
		let cancelled = false;
		const timer = setTimeout(() => {
			void (async () => {
				try {
					const r = await calculateEstimate({ ...toEstimateInput(state), currency, overrides: overrides() });
					if (!cancelled) {
						onLiveEstimate({ monthly: r.estimatedMonthlyCost, currency: r.currency });
						setLiveInput(r.inputTokens.total);
					}
				} catch {
					/* keep the last good value on transient errors */
				}
			})();
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [state, currency, models, onLiveEstimate]);

	const update = (patch: Partial<WizardState>) => setState((s) => ({ ...s, ...patch }));

	/** Load a QA test case into the wizard state (Test mode). */
	function applyTestCase(id: string) {
		setTestCaseId(id);
		setError(null);
		setResult(null);
		const tc = TEST_CASES.find((t) => t.id === id);
		if (!tc) return;
		setState((s) => ({ ...initialWizardState, modelId: s.modelId ?? models[0]?.modelId, ...tc.patch, stepIndex: 0 }));
	}

	function validateStep(index: number): string | null {
		const dm = state.decisionMaking;
		switch (index) {
			case 0:
				return state.contentTypes.length >= 1 ? null : 'Select at least one content type.';
			case 1:
				return state.interactionType ? null : 'Select an interaction type.';
			case 2: {
				const { perDay, perMonth, perYear } = state.volumetrics;
				for (const v of [perDay, perMonth, perYear])
					if (!Number.isInteger(v) || v < 0 || v > VOL_MAX)
						return `Volumetrics must be whole numbers from 0 to ${VOL_MAX.toLocaleString()}.`;
				return null;
			}
			case 4: {
				if (!dm.choice) return 'Choose whether the process makes decisions.';
				if (dm.choice === 'yes' && dm.decisionsSimple + dm.decisionsComplex < 1)
					return 'Enter at least one decision (Low or High impact).';
				return null;
			}
			case 5:
				return state.outputSize ? null : 'Select an output size.';
			default:
				return null;
		}
	}

	function next() {
		const msg = validateStep(state.stepIndex);
		if (msg) {
			setError(msg);
			return;
		}
		setError(null);
		if (state.stepIndex < TOTAL_INPUT_STEPS - 1) update({ stepIndex: state.stepIndex + 1 });
		else void submit();
	}
	function back() {
		setError(null);
		if (state.stepIndex > 0) update({ stepIndex: state.stepIndex - 1 });
	}
	/** Jump straight to a step from the clean-layout sidebar. */
	function goToStep(index: number) {
		if (index < 0 || index >= TOTAL_INPUT_STEPS || index === state.stepIndex) return;
		setError(null);
		update({ stepIndex: index });
	}

	async function submit() {
		setSubmitting(true);
		setError(null);
		try {
			setResult(await calculateEstimate({ ...toEstimateInput(state), currency, overrides: overrides() }));
		} catch (err) {
			if (err instanceof ValidationApiError) setError(err.fields.map((f) => `${f.field}: ${f.message}`).join('; '));
			else if (err instanceof ModelUnavailableError) {
				update({ modelId: models[0]?.modelId });
				setError('The selected model is unavailable. We selected a default — please reselect a model.');
			} else if (err instanceof ServiceUnavailableError) setError(`Service busy, retry in ${err.retryAfterSeconds}s.`);
			else setError(err instanceof Error ? err.message : 'Estimate failed.');
		} finally {
			setSubmitting(false);
		}
	}

	if (result) {
		const resultsScreen = (
			<ResultsScreen
				embedded={clean}
				result={result}
				models={models}
				daysPerMonth={params.daysPerMonth}
				daysPerYear={params.daysPerYear}
				onAdjust={() => {
					setResult(null);
					update({ stepIndex: 5 });
				}}
				onRestart={() => {
					setResult(null);
					setState({ ...initialWizardState, modelId: models[0]?.modelId });
					setError(null);
				}}
				onModelChange={async (modelId) => {
					update({ modelId });
					setSubmitting(true);
					try {
						setResult(
							await calculateEstimate({
								...toEstimateInput({ ...state, modelId }),
								currency,
								overrides: { params, model: models.find((m) => m.modelId === modelId) },
							}),
						);
						setError(null);
					} catch {
						setError('Could not recompute for that model.');
					} finally {
						setSubmitting(false);
					}
				}}
			/>
		);
		if (!clean) return resultsScreen;
		return (
			<div className="sg sg-wide">
				<div className="sc wiz2">
					<div className="wiz2-grid">
						<WizardSidebar
							activeStep={TOTAL_INPUT_STEPS}
							summaryActive
							onPick={(k) => {
								setResult(null);
								setError(null);
								update({ stepIndex: k });
							}}
						/>
						<div className="wmain">{resultsScreen}</div>
					</div>
				</div>
			</div>
		);
	}

	const i = state.stepIndex;

	return (
		<div className={`sg${clean ? ' sg-wide' : ''}`}>
			<div className={`sc${clean ? ' wiz2' : ''}`}>
				{testMode && (
					<div className="branch active" style={{ marginTop: 0, marginBottom: 14 }}>
						<label htmlFor="test-case" className="flbl" style={{ marginBottom: 6 }}>
							Test case (pre-fills from AI_Calculator_Test_Cases)
						</label>
						<select
							id="test-case"
							value={testCaseId}
							onChange={(e) => applyTestCase(e.target.value)}
							style={{ display: 'block', width: '100%', fontSize: 13, padding: 8, borderRadius: 8 }}
						>
							<option value="">— Select a test case to pre-fill —</option>
							{TEST_CASES.map((t) => (
								<option key={t.id} value={t.id}>
									{t.label}
								</option>
							))}
						</select>
						<div className="note" style={{ marginTop: 8 }}>
							Loads the case inputs, then step through and Calculate to compare against the Excel Guided tab.
						</div>
					</div>
				)}

				<div className={clean ? 'wiz2-grid' : undefined}>
					{clean && <WizardSidebar activeStep={i} summaryActive={false} onPick={goToStep} />}
					<div className={clean ? 'wmain' : undefined}>
						{!clean && (
							<>
								<div className="cardtop">
									<span className="stepno">
										Step {i + 1} of {TOTAL_INPUT_STEPS}
									</span>
								</div>
								<div
									className="prog"
									role="progressbar"
									aria-valuenow={i + 1}
									aria-valuemin={1}
									aria-valuemax={TOTAL_INPUT_STEPS}
									aria-label={`Step ${i + 1} of ${TOTAL_INPUT_STEPS}`}
								>
									{STEP_DOTS.map((k) => (
										<span key={k} className={k <= i ? 'd' : ''} aria-hidden="true" />
									))}
								</div>
							</>
						)}

						<h2 className="stitle" id="step-heading" ref={stepHeadingRef} tabIndex={-1}>
							{STEP_Q[i]}
						</h2>

						<div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
							{announcement}
						</div>

						{i === 0 && <Step1Content contentTypes={state.contentTypes} update={update} />}
						{i === 1 && (
							<Step2Interaction
								interactionType={state.interactionType}
								bulkSize={state.bulkSize}
								turnsPerConversation={state.turnsPerConversation}
								liveInput={liveInput}
								update={update}
							/>
						)}
						{i === 2 && (
							<Step3Inputs
								volumetrics={state.volumetrics}
								contentTypes={state.contentTypes}
								dataUnits={state.dataUnits}
								params={params}
								update={update}
							/>
						)}
						{i === 3 && <Step4Context additionalContext={state.additionalContext} update={update} />}
						{i === 4 && <Step5Decisions decisionMaking={state.decisionMaking} update={update} />}
						{i === 5 && (
							<Step6Output
								outputSize={state.outputSize}
								outputSizeCount={state.outputSizeCount}
								expert={expert}
								modelId={state.modelId}
								pricingTier={state.pricingTier}
								cachedInputRatio={state.cachedInputRatio}
								thinkingTokensPerTurn={state.thinkingTokensPerTurn}
								models={models}
								update={update}
							/>
						)}

						{error && (
							<div role="alert" className="alert" aria-live="assertive">
								{error}
							</div>
						)}

						{/* Navigation at the BOTTOM of the card (revised design). */}
						<div className={`navrow${clean ? ' wiz2-nav' : ''}`}>
							{i > 0 && (
								<button type="button" className="navbtn back" onClick={back} disabled={submitting}>
									Back
								</button>
							)}
							<button type="button" className="navbtn next" onClick={next} disabled={submitting}>
								{i === TOTAL_INPUT_STEPS - 1 ? (submitting ? 'Calculating…' : 'Calculate my estimate') : 'Next'}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
